import React, { useContext } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Phone, ArrowRight, Shield, Award, Clock } from 'lucide-react';
import { ContentContext } from '../App';

const HeroSection = () => {
  const { t, language } = useLanguage();
  const content = useContext(ContentContext);
  const company = content?.company || {};

  const stats = [
    { icon: Shield, value: '100%', label: { en: 'Satisfaction', zh: '满意度' } },
    { icon: Award, value: `${company.years_in_business || 20}+`, label: { en: 'Years Experience', zh: '年经验' } },
    { icon: Clock, value: '1hr', label: { en: 'Response Time', zh: '响应时间' } }
  ];

  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-slate-900">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1635424824800-692767998d07?w=1920')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-1/4 right-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-10 w-96 h-96 bg-slate-600/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            {/* Promo Badge */}
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2">
              <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
              <span className="text-amber-400 text-sm font-medium">
                {t.promo.title} - {t.promo.subtitle}
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              {t.hero.title.split(' ').map((word, i) => (
                <span key={i}>
                  {word === 'GTA' ? (
                    <span className="text-amber-400">{word}</span>
                  ) : (
                    word
                  )}{' '}
                </span>
              ))}
            </h1>

            <p className="text-lg text-slate-400 max-w-xl leading-relaxed">
              {t.hero.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold text-lg px-8 group"
              >
                {t.hero.cta}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <a href={`tel:${company.phone || '416-688-9988'}`}>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white text-lg px-8 w-full sm:w-auto"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {t.hero.ctaSecondary}
                </Button>
              </a>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-8 pt-8 border-t border-slate-800">
              {stats.map((stat, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center">
                    <stat.icon className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{stat.value}</div>
                    <div className="text-sm text-slate-400">
                      {stat.label[language]}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero Image Card */}
          <div className="hidden lg:block relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
              <img
                src="https://images.unsplash.com/photo-1633759593085-1eaeb724fc88?w=800"
                alt="Professional Roofing"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
              
              {/* Floating Card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                    <Shield className="w-6 h-6 text-slate-900" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">
                      {language === 'en' ? 'Licensed & Insured' : '持牌并投保'}
                    </div>
                    <div className="text-slate-300 text-sm">
                      {language === 'en' ? 'Your protection guaranteed' : '保障您的权益'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative Badge */}
            <div className="absolute -top-4 -right-4 bg-amber-500 text-slate-900 font-bold px-4 py-2 rounded-lg shadow-lg transform rotate-3">
              {company.years_in_business || 20}+ {language === 'en' ? 'Years' : '年'}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
